import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Campaign, Site, Technicentre, Prestation } from '../types';
import { campaignsApi, sitesApi, technicentresApi, prestationsApi } from '../services/api';
import { useAuth } from './AuthContext';

// ===== ContexteDonnees =====

interface DataContextType {
  campaigns: Campaign[];
  sites: Site[];
  technicentres: Technicentre[];
  prestations: Prestation[];
  loading: boolean;
  refreshAll: () => Promise<void>;
  refreshCampaigns: () => Promise<void>;
  refreshSites: () => Promise<void>;
  refreshTechnicentres: () => Promise<void>;
  refreshPrestations: () => Promise<void>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

interface DataProviderProps {
  children: ReactNode;
}

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [technicentres, setTechnicentres] = useState<Technicentre[]>([]);
  const [prestations, setPrestations] = useState<Prestation[]>([]);
  const [loading, setLoading] = useState(false);
  const hasLoadedRef = useRef(false);

  const isAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    isAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  const refreshCampaigns = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    try {
      const data = await campaignsApi.getAll();
      setCampaigns(data);
    } catch (error) {
      console.error('Error refreshing campaigns:', error);
    }
  }, []);

  const refreshSites = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    try {
      const data = await sitesApi.getAll();
      setSites(data);
    } catch (error) {
      console.error('Error refreshing sites:', error);
    }
  }, []);

  const refreshTechnicentres = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    try {
      const data = await technicentresApi.getAll();
      setTechnicentres(data);
    } catch (error) {
      console.error('Error refreshing technicentres:', error);
    }
  }, []);

  const refreshPrestations = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    try {
      const data = await prestationsApi.getAll();
      setPrestations(data);
    } catch (error) {
      console.error('Error refreshing prestations:', error);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    if (!isAuthenticatedRef.current) return;
    setLoading(true);
    try {
      const [campaignsData, sitesData, technicentresData, prestationsData] = await Promise.all([
        campaignsApi.getAll(),
        sitesApi.getAll(),
        technicentresApi.getAll(),
        prestationsApi.getAll(),
      ]);
      setCampaigns(campaignsData);
      setSites(sitesData);
      setTechnicentres(technicentresData);
      setPrestations(prestationsData);
    } catch (error) {
      console.error('Error refreshing all data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      void refreshAll();
    } else if (!isAuthenticated) {
      hasLoadedRef.current = false;
      setCampaigns([]);
      setSites([]);
      setTechnicentres([]);
      setPrestations([]);
    }
  }, [isAuthenticated, refreshAll]);

  useEffect(() => {
    const handleDataUpdate = (event: CustomEvent) => {
      if (!isAuthenticatedRef.current) return;

      const { type } = event.detail || {};
      if (type === 'campaigns') {
        void refreshCampaigns();
      } else if (type === 'sites') {
        void refreshSites();
      } else if (type === 'technicentres') {
        void refreshTechnicentres();
      } else if (type === 'prestations') {
        void refreshPrestations();
      } else {
        void refreshAll();
      }
    };

    window.addEventListener('dataUpdated', handleDataUpdate as EventListener);
    return () => {
      window.removeEventListener('dataUpdated', handleDataUpdate as EventListener);
    };
  }, [refreshAll, refreshCampaigns, refreshSites, refreshTechnicentres, refreshPrestations]);

  return (
    <DataContext.Provider
      value={{
        campaigns,
        sites,
        technicentres,
        prestations,
        loading,
        refreshAll,
        refreshCampaigns,
        refreshSites,
        refreshTechnicentres,
        refreshPrestations,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataContext;
